/**
 * Session Modal - Multi-Interview Session Configuration
 * Allows users to select multiple interview types with individual timers
 */

import React, { useState, useEffect } from 'react';

interface InterviewType {
  id: string;
  name: string;
  icon: string;
}

interface TimeOption {
  value: number;
  label: string;
}

interface SelectedInterview {
  id: number;
  type: string;
  name: string;
  icon: string;
  duration: number;
  difficulty: string;
  order?: number;
}

interface InterviewConfig {
  isMultiSession: boolean;
  interviews: SelectedInterview[];
  totalDuration: number;
  sessionId: string;
  templateId?: string;
}

interface SessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStart: (config: InterviewConfig) => void;
}

const INTERVIEW_TYPES: InterviewType[] = [
  { id: 'system_design', name: 'System Design', icon: '🏗️' },
  { id: 'coding', name: 'Live Coding', icon: '💻' },
  { id: 'technical', name: 'Technical Q&A', icon: '🎯' },
  { id: 'architecture_review', name: 'Architecture Review', icon: '📐' },
  { id: 'behavioural', name: 'Behavioural', icon: '🗣️' },
  { id: 'case_study', name: 'Case Study Prep', icon: '📑' },
];

const TIME_OPTIONS: TimeOption[] = [
  { value: 1, label: '1 min' },
  { value: 5, label: '5 min' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '1 hour' },
];

const MAX_TOTAL_TIME = 90; // 1 hour 30 minutes

const SessionModal: React.FC<SessionModalProps> = ({
  isOpen,
  onClose,
  onStart,
}) => {
  const [selectedInterviews, setSelectedInterviews] = useState<
    SelectedInterview[]
  >([]);
  const [error, setError] = useState<string>('');

  // Calculate total time
  const totalTime = selectedInterviews.reduce(
    (sum, interview) => sum + interview.duration,
    0
  );

  useEffect(() => {
    if (totalTime > MAX_TOTAL_TIME) {
      setError(
        `Total time exceeds maximum of ${MAX_TOTAL_TIME} minutes (1hr 30min)`
      );
    } else {
      setError('');
    }
  }, [totalTime]);

  const handleAddInterview = (typeId: string) => {
    const type = INTERVIEW_TYPES.find((t) => t.id === typeId);
    if (!type) return;

    // Check if already added
    if (selectedInterviews.some((i) => i.type === typeId)) {
      setError('This interview type is already added');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const newInterview: SelectedInterview = {
      id: Date.now(),
      type: typeId,
      name: type.name,
      icon: type.icon,
      duration: 15,
      difficulty: 'medium',
    };

    setSelectedInterviews([...selectedInterviews, newInterview]);
  };

  const handleRemoveInterview = (id: number) => {
    setSelectedInterviews(selectedInterviews.filter((i) => i.id !== id));
  };

  const handleUpdateInterview = (
    id: number,
    field: string,
    value: string | number
  ) => {
    setSelectedInterviews(
      selectedInterviews.map((interview) =>
        interview.id === id
          ? {
              ...interview,
              [field]:
                field === 'duration' ? parseInt(String(value), 10) : value,
            }
          : interview
      )
    );
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newList = [...selectedInterviews];
    [newList[index - 1], newList[index]] = [newList[index], newList[index - 1]];
    setSelectedInterviews(newList);
  };

  const handleMoveDown = (index: number) => {
    if (index === selectedInterviews.length - 1) return;
    const newList = [...selectedInterviews];
    [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    setSelectedInterviews(newList);
  };

  const handleStartSession = () => {
    if (selectedInterviews.length === 0) {
      setError('Please add at least one interview type');
      return;
    }
    if (totalTime > MAX_TOTAL_TIME) {
      setError(`Total time exceeds maximum of ${MAX_TOTAL_TIME} minutes`);
      return;
    }

    onStart({
      isMultiSession: true,
      interviews: selectedInterviews.map((interview, index) => ({
        ...interview,
        order: index + 1,
      })),
      totalDuration: totalTime,
      sessionId: `session_${Date.now()}`,
    });
  };

  const formatTime = (minutes: number): string => {
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}hr ${mins}min` : `${hours}hr`;
    }
    return `${minutes}min`;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-3 sm:p-5 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl sm:rounded-3xl w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-white/10 bg-black/20">
          <h2 className="text-white text-lg sm:text-2xl font-bold">
            🎯 Create Interview Session
          </h2>
          <button
            className="bg-white/10 hover:bg-red-500/80 text-white w-8 h-8 sm:w-9 sm:h-9 rounded-full text-base sm:text-lg transition-all duration-200 hover:scale-110"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className="p-4 sm:p-7 overflow-y-auto flex-1">
          {/* Interview Type Selector */}
          <div className="mb-7">
            <h3 className="text-slate-200 text-base font-semibold mb-4 uppercase tracking-wide">
              Add Interview Types
            </h3>
            <div className="flex flex-wrap gap-2 sm:gap-2.5">
              {INTERVIEW_TYPES.map((type) => (
                <button
                  key={type.id}
                  className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl border-2 transition-all duration-200 text-xs sm:text-sm ${
                    selectedInterviews.some((i) => i.type === type.id)
                      ? 'opacity-40 cursor-not-allowed bg-indigo-500/20 border-indigo-500/40'
                      : 'bg-indigo-500/20 border-indigo-500/40 hover:bg-indigo-500/40 hover:border-indigo-500 hover:-translate-y-0.5 text-white'
                  }`}
                  onClick={() => handleAddInterview(type.id)}
                  disabled={selectedInterviews.some((i) => i.type === type.id)}
                >
                  <span className="text-lg sm:text-xl">{type.icon}</span>
                  <span className="text-xs sm:text-sm whitespace-nowrap">
                    {type.name}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Selected Interviews */}
          <div className="mb-6">
            <h3 className="text-slate-200 text-base font-semibold mb-4 uppercase tracking-wide">
              Session Order
            </h3>
            {selectedInterviews.length === 0 ? (
              <p className="text-slate-500 text-center py-8 px-5 bg-white/3 rounded-xl border-2 border-dashed border-white/10">
                Click on interview types above to add them to your session
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {selectedInterviews.map((interview, index) => (
                  <div
                    key={interview.id}
                    className="flex items-center gap-3 p-4 bg-white/5 rounded-xl border border-white/10 transition-all duration-200 hover:bg-white/8 hover:border-indigo-500/40"
                  >
                    <div className="flex flex-col items-center gap-0.5">
                      <button
                        className="bg-white/10 hover:bg-indigo-500/50 text-slate-400 w-6 h-[18px] rounded text-[10px] transition-all duration-200 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                      >
                        ▲
                      </button>
                      <span className="text-indigo-400 font-bold text-lg min-w-[24px] text-center">
                        {index + 1}
                      </span>
                      <button
                        className="bg-white/10 hover:bg-indigo-500/50 text-slate-400 w-6 h-[18px] rounded text-[10px] transition-all duration-200 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === selectedInterviews.length - 1}
                      >
                        ▼
                      </button>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-2.5 flex-1 min-w-0 sm:min-w-[150px]">
                      <span className="text-lg sm:text-xl flex-shrink-0">
                        {interview.icon}
                      </span>
                      <span className="text-white font-medium text-sm sm:text-base truncate">
                        {interview.name}
                      </span>
                    </div>

                    <div className="flex gap-2 sm:gap-2.5 flex-wrap">
                      <select
                        value={interview.duration}
                        onChange={(e) =>
                          handleUpdateInterview(
                            interview.id,
                            'duration',
                            e.target.value
                          )
                        }
                        className="px-2 sm:px-3 py-1.5 sm:py-2 bg-black/30 border border-white/20 rounded-lg text-white text-xs sm:text-sm cursor-pointer transition-all duration-200 hover:border-indigo-500 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 flex-1 sm:flex-none min-w-[80px]"
                      >
                        {TIME_OPTIONS.map((option) => (
                          <option
                            key={option.value}
                            value={option.value}
                            className="bg-slate-800 text-white"
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>

                      <select
                        value={interview.difficulty}
                        onChange={(e) =>
                          handleUpdateInterview(
                            interview.id,
                            'difficulty',
                            e.target.value
                          )
                        }
                        className="px-2 sm:px-3 py-1.5 sm:py-2 bg-black/30 border border-white/20 rounded-lg text-white text-xs sm:text-sm cursor-pointer transition-all duration-200 hover:border-indigo-500 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 flex-1 sm:flex-none min-w-[90px]"
                      >
                        <option
                          value="medium"
                          className="bg-slate-800 text-white"
                        >
                          Medium
                        </option>
                        <option
                          value="hard"
                          className="bg-slate-800 text-white"
                        >
                          Hard
                        </option>
                      </select>

                      <button
                        className="bg-red-500/20 hover:bg-red-500/40 text-red-400 w-7 h-7 sm:w-8 sm:h-8 rounded-lg text-xs sm:text-sm transition-all duration-200 hover:scale-110 flex-shrink-0"
                        onClick={() => handleRemoveInterview(interview.id)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Time Summary */}
          <div className="mb-5">
            <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2.5">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  totalTime > MAX_TOTAL_TIME
                    ? 'bg-gradient-to-r from-red-500 to-red-400'
                    : 'bg-gradient-to-r from-emerald-500 to-emerald-400'
                }`}
                style={{
                  width: `${Math.min(
                    (totalTime / MAX_TOTAL_TIME) * 100,
                    100
                  )}%`,
                }}
              />
            </div>
            <div className="flex justify-between text-sm">
              <span
                className={
                  totalTime > MAX_TOTAL_TIME
                    ? 'text-red-400 font-semibold'
                    : 'text-emerald-400 font-semibold'
                }
              >
                Total: {formatTime(totalTime)}
              </span>
              <span className="text-slate-500 font-normal">
                Max: {formatTime(MAX_TOTAL_TIME)}
              </span>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-400/40 text-red-200 px-4 py-3 rounded-lg text-sm text-center">
              {error}
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 p-4 sm:p-5 border-t border-white/10 bg-black/20">
          <button
            className="px-4 sm:px-6 py-2.5 sm:py-3 bg-white/10 hover:bg-white/15 border border-white/20 rounded-lg text-white text-sm sm:text-base cursor-pointer transition-all duration-200"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-5 sm:px-7 py-2.5 sm:py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 border-none rounded-lg text-white text-sm sm:text-base font-semibold cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            onClick={handleStartSession}
            disabled={
              selectedInterviews.length === 0 || totalTime > MAX_TOTAL_TIME
            }
          >
            Start Session ({selectedInterviews.length} interview
            {selectedInterviews.length !== 1 ? 's' : ''})
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionModal;
